import { useState, useEffect } from 'react'
import { api, formatDate } from '../api'
import RinaContextCard from '../components/RinaContextCard'
import EventCardPublic from '../components/EventCardPublic'
import { useTheme } from '../theme'
import { useActionsPortail } from '../contexts/navigation'
import { useAuth } from '../AuthContext'
import useMarchesPublics from '../hooks/useMarchesPublics'
import PageHub from '../components/PageHub'

function ContenuAccueilClient() {
  const { isAuthenticated, user } = useAuth()
  const { navigate: onNavigate, openMarche } = useActionsPortail()
  const marchesProp = useMarchesPublics()
  const onSelectEvent = (event) => openMarche(event.id)
  const { isWeb3 } = useTheme()
  // Les marches sont fournies par App.jsx (fetch unique partage avec l'intro).
  // Repli local si le prop n'est pas fourni (retrocompat). null = chargement.
  const [localMarches, setLocalMarches] = useState(null)
  useEffect(() => {
    if (marchesProp !== undefined) return
    api.getMarches()
      .then(data => setLocalMarches(Array.isArray(data) ? data : []))
      .catch(() => setLocalMarches([]))
  }, [marchesProp])

  const source = marchesProp !== undefined ? marchesProp : localMarches
  const loading = source == null
  const marches = Array.isArray(source) ? source : []

  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const tm = 'text-ink/60'
  const cardBg = isWeb3 ? 'bg-white/5 border-white/10' : 'glass-surface'

  const now = new Date()
  const isFeaturedNow = (e) => e?.featured && (!e.featuredUntil || new Date(e.featuredUntil) >= now)
  const featuredTous = marches.filter(isFeaturedNow)
  const othersTous = marches.filter(e => !isFeaturedNow(e))

  // Hero = prochain rendez-vous (1er a la une, sinon 1er a venir)
  const hero = featuredTous[0] || othersTous[0]
  // S62 : le hero ne se repete pas dans les listes en dessous (retour Daniel :
  // « un seul evenement en doublon ne sert a rien »).
  const featured = featuredTous.filter(e => e !== hero)
  const others = othersTous.filter(e => e !== hero)
  // `Event.date` est un JOUR calendaire (`@db.Date`) : il se lit en UTC, sinon
  // la Martinique (UTC-4) affiche la veille. C'est le bug remonte par Enzo — la
  // grande carte disait « vendredi 4 » quand la petite disait « samedi 5 » pour
  // le meme evenement, celle-ci passant deja par `formatDate`. Voir la regle en
  // tete de `api.js`.
  const heroDate = formatDate(hero?.date) || null

  return (
    <>
      {/* Carte RINA contextuelle — en tete de l'accueil (ordre du prototype v2) */}
      <RinaContextCard
        tc={tc}
       
        message={isAuthenticated && user?.prenom
          ? `${user.prenom}, découvre ce qu'on prépare pour toi ! 🥭`
          : 'Bienvenue sur Lyannaj ! Explorez le marché et les producteurs à votre rythme — créez un compte quand vous voulez commander ou garder vos favoris.'}
      />

      {/* HERO — prochain rendez-vous (style prototype : glass vert sombre) */}
      {!isWeb3 && !loading && hero && (
        <button
          onClick={() => onSelectEvent?.(hero)}
          className="relative w-full text-left rounded-[26px] px-[18px] pt-[18px] pb-4 mb-3 overflow-hidden animate-fade-up"
          style={{
            background: 'rgb(var(--c-green) / 0.88)',
            border: '1px solid rgb(var(--c-creme) / 0.16)',
            boxShadow: '0 12px 40px rgb(4 25 24 / 0.32)',
          }}
        >
          {/* Barre d'accent du module (prototype v5) */}
          <div
            className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgb(var(--c-teal) / 0.4), transparent 70%)' }}
          />
          {/* Badge du module qui presente l'evenement (DESIGN-SYSTEM v4 MODULE_THEME) — en haut a droite */}
          <span className="absolute top-5 right-5 z-[1] inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold"
            style={{ background: 'rgb(var(--c-teal) / 0.22)', color: '#5eead4', border: '1px solid rgb(var(--c-teal) / 0.45)' }}>
            🎪 Évènements
          </span>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-primary pr-24">Prochain rendez-vous</p>
          <p className="font-serif text-[26px] leading-tight text-creme mt-2">{hero.nom || hero.lieu || 'Évènement'}</p>
          <p className="text-[12.5px] text-creme/75 mt-1">
            {[heroDate, hero.lieu].filter(Boolean).join(' · ') || 'Bientôt'}
          </p>
          <span className="mt-3.5 block w-full text-center py-3 rounded-full font-extrabold text-[13.5px] text-green bg-primary">
            Voir
          </span>
        </button>
      )}

      {loading ? (
        <div className="space-y-3">
          <div className={`rounded-2xl h-44 animate-pulse bg-ink/5`} />
          <div className={`rounded-2xl h-32 animate-pulse bg-ink/5`} />
        </div>
      ) : (
        <>
          {/* Section A la une (featured) */}
          {featured.length > 0 && (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2.5 mt-5 text-primary">
                À la une
              </p>
              <div className="flex flex-col gap-3 mb-5">
                {featured.map(e => (
                  <EventCardPublic
                    key={e.id}
                    event={e}
                    variant="featured"
                    onClick={() => onSelectEvent?.(e)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Section Prochains evenements */}
          {others.length > 0 && (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-2.5 mt-1 text-teal">
                En ce moment
              </p>
              <div className="flex flex-col gap-3 mb-5">
                {others.map(e => (
                  <EventCardPublic
                    key={e.id}
                    event={e}
                    onClick={() => onSelectEvent?.(e)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Empty state */}
          {marches.length === 0 && (
            <div className={`rounded-2xl p-6 mt-2 mb-4 text-center animate-fade-up ${cardBg}`}>
              <span className="text-4xl block mb-3">🎪</span>
              <p className={`text-sm font-semibold mb-1 ${tc}`}>Aucun évènement à venir</p>
              <p className={`text-xs ${ts}`}>
                Les prochains évènements apparaîtront ici dès qu'ils seront planifiés
              </p>
            </div>
          )}
        </>
      )}

      {/* Visiteur : pas de bandeau d'incitation redondant — c'est RINA (en tete)
          qui accueille le visiteur (prototype v5, §5 quater). */}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageAccueilClient() {
  return <PageHub><ContenuAccueilClient /></PageHub>
}
